// A step gradient shader that creates discrete color transitions
// Users can override by registering with the same name at runtime
const defaultStepGradient = `
precision mediump float;
uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_scale;
uniform float u_seed;
uniform vec2 u_size;
uniform vec2 u_pos;
uniform float u_spacing;
uniform float u_radius;
uniform float u_borderwidth;
uniform float u_x_num;
uniform float u_y_num;
uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;
varying vec2 vUv;

float random(vec2 co) {
    return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

vec3 getStepColor(float t) {
    // Ensure t is in [0,1] range
    t = clamp(t, 0.0, 1.0);
    
    // Create 6 discrete steps
    float step = floor(t * 6.0);
    
    if (step < 1.0) return u_color_0;
    if (step < 2.0) return u_color_1;
    if (step < 3.0) return u_color_2;
    if (step < 4.0) return u_color_3;
    if (step < 5.0) return u_color_4;
    return u_color_5;
}

void main(){
    vec2 st = gl_FragCoord.xy / u_resolution;
    
    // Apply scale transformation
    st = (st - 0.5) * u_scale + 0.5;
    
    // Time-based animation
    float timeOffset = (u_time * u_speed) * 0.01;
    
    // Create step gradient based on different directions
    float gradientValue = 0.0;
    
    // Vertical gradient with time animation
    gradientValue = st.y + sin(timeOffset + st.x * u_x_num) * 0.1;
    
    // Add horizontal influence
    gradientValue += st.x * 0.3;
    
    // Add noise based on seed
    vec2 noisePos = st * u_y_num + vec2(u_seed);
    float noise = random(noisePos) * 0.2;
    gradientValue += noise;
    
    // Apply spacing to create more distinct steps
    gradientValue = floor(gradientValue * u_spacing) / u_spacing;
    
    // Get the step color
    vec3 col = getStepColor(fract(gradientValue));
    
    // Add border effect using radius parameter
    vec2 center = vec2(0.5);
    float dist = distance(st, center);
    float border = smoothstep(u_radius - u_borderwidth, u_radius, dist);
    
    // Mix with background color at borders
    vec3 bgColor = mix(u_color_0, u_color_5, 0.1) * 0.1;
    col = mix(col, bgColor, border);
    
    gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'StepGradient',
  shader: {
    frag: defaultStepGradient,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#ff6a88', '#ff99ac', '#f6d365', '#8fd3f4', '#84fab0', '#c471ed'],
    },
    {
      key: 'u_x_num',
      type: 'number',
      label: 'Wave Frequency',
      min: 1,
      max: 20,
      step: 0.5,
      default: 5,
    },
    {
      key: 'u_y_num',
      type: 'number',
      label: 'Noise Scale',
      min: 1,
      max: 50,
      step: 1,
      default: 10,
    },
    {
      key: 'u_radius',
      type: 'number',
      label: 'Vignette Size',
      min: 0,
      max: 2,
      step: 0.01,
      default: 1.5,
    },
    {
      key: 'u_borderwidth',
      type: 'number',
      label: 'Vignette Softness',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.3,
    },
    { key: 'u_scale', type: 'number', label: 'Scale', min: 0.1, max: 5, step: 0.1, default: 1 },
    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 10, step: 0.1, default: 1 },
    {
      key: 'u_spacing',
      type: 'number',
      label: 'Step Count',
      min: 1,
      max: 20,
      step: 0.5,
      default: 6,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(StepGradient, colors=#ff6a88,#ff99ac,#f6d365,#8fd3f4,#84fab0,#c471ed; u_spacing=6; u_speed=1; animated=true)',
    'dynamic-shader(StepGradient, colors=#667eea,#764ba2,#f093fb,#f5576c; u_spacing=4; u_x_num=3; u_y_num=15; animated=true; u_speed=0.5)',
  ],
};

export default shader;
